import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import { ArrowBack, Save } from "@mui/icons-material";
import { useCampaign, useCreateCampaign, useUpdateCampaign } from "@/hooks/useCampaigns";
import { CAMPAIGN_STATUS_LABELS, type CampaignStatus } from "@/types";
import ChipListEditor from "@/components/ChipListEditor";

export default function CampaignEdit() {
  const { campaignId } = useParams<{ campaignId: string }>();
  const isNew = !campaignId;
  const navigate = useNavigate();

  const { data: existing, isLoading } = useCampaign(isNew ? undefined : campaignId);
  const createMutation = useCreateCampaign();
  const updateMutation = useUpdateCampaign(campaignId ?? "");

  const [form, setForm] = useState<Record<string, unknown>>({
    name: "",
    description: "",
    status: "planned" as CampaignStatus,
    goal: "",
    target_segment: "",
    owner_agent: "AI-02",
    start_date: "",
    end_date: "",
    tags: [],
    notes: "",
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success" as "success" | "error",
  });

  useEffect(() => {
    if (existing) {
      setForm({
        name: existing.name,
        description: existing.description ?? "",
        status: existing.status,
        goal: existing.goal ?? "",
        target_segment: existing.target_segment ?? "",
        owner_agent: existing.owner_agent ?? "",
        start_date: existing.start_date ?? "",
        end_date: existing.end_date ?? "",
        tags: existing.tags ?? [],
        notes: existing.notes ?? "",
      });
    }
  }, [existing]);

  const handleSave = async () => {
    const data = { ...form };
    for (const k of Object.keys(data)) {
      if (data[k] === "") data[k] = null;
    }
    if ((data.tags as string[])?.length === 0) data.tags = null;

    try {
      if (isNew) {
        const c = await createMutation.mutateAsync(data);
        navigate(`/campaigns/${c.id}`);
      } else {
        await updateMutation.mutateAsync(data);
        setSnackbar({ open: true, message: "Campaign saved", severity: "success" });
      }
    } catch {
      setSnackbar({ open: true, message: "Failed to save", severity: "error" });
    }
  };

  const update = (field: string, value: unknown) => setForm((f) => ({ ...f, [field]: value }));

  if (!isNew && isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate(isNew ? "/campaigns" : `/campaigns/${campaignId}`)}
        >
          Back
        </Button>
        <Typography variant="h4" sx={{ flex: 1 }}>
          {isNew ? "New Campaign" : `Edit: ${existing?.name ?? ""}`}
        </Typography>
        <Button
          variant="contained"
          startIcon={<Save />}
          onClick={handleSave}
          disabled={isPending || !form.name}
        >
          {isPending ? "Saving…" : "Save"}
        </Button>
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2 }}>
        <Card>
          <CardContent sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Typography variant="h6">Campaign</Typography>
            <TextField
              label="Name"
              value={form.name}
              onChange={(e) => update("name", e.target.value)}
              fullWidth
              required
            />
            <TextField
              label="Description"
              value={form.description}
              onChange={(e) => update("description", e.target.value)}
              fullWidth
              multiline
              minRows={2}
            />
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={form.status}
                label="Status"
                onChange={(e) => update("status", e.target.value)}
              >
                {Object.entries(CAMPAIGN_STATUS_LABELS).map(([k, v]) => (
                  <MenuItem key={k} value={k}>
                    {v}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Goal"
              value={form.goal}
              onChange={(e) => update("goal", e.target.value)}
              fullWidth
              multiline
              minRows={2}
              placeholder="What does success look like?"
            />
            <TextField
              label="Target segment"
              value={form.target_segment}
              onChange={(e) => update("target_segment", e.target.value)}
              fullWidth
              placeholder="e.g. tech startups in London, 10–50 staff"
            />
          </CardContent>
        </Card>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Card>
            <CardContent sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Typography variant="h6">Schedule & Owner</Typography>
              <Box sx={{ display: "flex", gap: 2 }}>
                <TextField
                  type="date"
                  label="Start date"
                  InputLabelProps={{ shrink: true }}
                  value={form.start_date}
                  onChange={(e) => update("start_date", e.target.value)}
                  fullWidth
                />
                <TextField
                  type="date"
                  label="End date"
                  InputLabelProps={{ shrink: true }}
                  value={form.end_date}
                  onChange={(e) => update("end_date", e.target.value)}
                  fullWidth
                />
              </Box>
              <TextField
                label="Owner (agent code or person)"
                value={form.owner_agent}
                onChange={(e) => update("owner_agent", e.target.value)}
                fullWidth
                placeholder="AI-02 or Steve / Evelina"
              />
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <ChipListEditor
                label="Tags"
                values={(form.tags as string[]) ?? []}
                onChange={(v) => update("tags", v)}
              />
            </CardContent>
          </Card>

          <Card>
            <CardContent sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Typography variant="h6">Notes</Typography>
              <TextField
                value={form.notes}
                onChange={(e) => update("notes", e.target.value)}
                fullWidth
                multiline
                minRows={4}
                placeholder="Anything that doesn't fit above"
              />
            </CardContent>
          </Card>
        </Box>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}
